import React, { useState, useEffect } from 'react';
import './HierarchicalPricingManagement.css';
import { useAuth } from '../contexts/AuthContext';
import { useScope } from '../contexts/ScopeContext';
import { API_ENDPOINTS } from '../config/apiRouting';
import { getUserRole } from '../utils/roleUtils';

interface MenuItem {
  menuItemId: string;
  name: string;
  description: string;
  basePrice: number;
  price: number;
  category: string;
  enterpriseId: string;
}

interface PricingOverride {
  pricingId: string;
  menuItemId: string;
  levelType: 'enterprise' | 'concept' | 'franchisee' | 'store';
  levelId: string;
  price: number;
  effectiveDate: string;
  createdBy: string;
  isActive: boolean;
}

interface Store {
  storeId: string;
  name: string;
  code: string;
  conceptId?: string;
  franchiseeId?: string;
  enterpriseId: string;
}

interface Concept {
  conceptId: string;
  name: string;
  enterpriseId: string;
}

interface Franchisee {
  franchiseeId: string;
  name: string;
  enterpriseId: string;
}

interface Enterprise {
  enterpriseId: string;
  name: string;
}

const HierarchicalPricingManagement: React.FC = () => {
  const { getIdToken, user, session } = useAuth();
  const { enterpriseId: scopeEnterpriseId } = useScope();
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState<string>('');
  const [userPickedEnterprise, setUserPickedEnterprise] = useState(false);

  // Follow the header enterprise selector until the user picks locally.
  useEffect(() => {
    if (userPickedEnterprise || !scopeEnterpriseId) return;
    if (selectedEnterprise === scopeEnterpriseId) return;
    if (enterprises.length > 0 && !enterprises.some((e) => e.enterpriseId === scopeEnterpriseId)) return;
    setSelectedEnterprise(scopeEnterpriseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEnterpriseId, userPickedEnterprise, enterprises]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [franchisees, setFranchisees] = useState<Franchisee[]>([]);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('');
  const [selectedLevel, setSelectedLevel] = useState<'concept' | 'franchisee' | 'store'>('store');
  const [selectedLevelId, setSelectedLevelId] = useState<string>('');  
  const [newPrice, setNewPrice] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [pricingOverrides, setPricingOverrides] = useState<PricingOverride[]>([]);
  const [effectivePrices, setEffectivePrices] = useState<{[key: string]: number}>({});

  const loadEnterprises = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(API_ENDPOINTS.enterprises(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        // Handle both array response and {data: []} wrapped response
        const enterpriseList = Array.isArray(data) ? data : (data.data || data.enterprises || []);
        setEnterprises(enterpriseList);

        // Auto-select: prefer header scope if it's in the list, else first enterprise for
        // non-super-admins or the sole enterprise for single-enterprise users.
        const userRole = getUserRole(user, session);
        const scopeMatch = scopeEnterpriseId && enterpriseList.some((e: Enterprise) => e.enterpriseId === scopeEnterpriseId);
        if (scopeMatch) {
          setSelectedEnterprise(scopeEnterpriseId);
        } else if (enterpriseList.length === 1) {
          setSelectedEnterprise(enterpriseList[0].enterpriseId);
        } else if (enterpriseList.length > 1 && userRole !== 'super_admin') {
          setSelectedEnterprise(enterpriseList[0].enterpriseId);
        }
        // Super admins with multiple enterprises: require manual selection
      }
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setMessage('Error loading enterprises');
    }
  };

  useEffect(() => {
    loadEnterprises();
  }, []);

  useEffect(() => {
    if (selectedEnterprise) {
      loadData();
    }
  }, [selectedEnterprise]);

  useEffect(() => {
    if (selectedMenuItem && stores.length > 0) {
      loadEffectivePrices();
    }
  }, [selectedMenuItem, stores]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      
      // Load menu items, stores, concepts, franchisees using microservices
      const [menuResponse, storesResponse, conceptsResponse, franchiseesResponse] = await Promise.all([
        fetch(API_ENDPOINTS.menuItems(selectedEnterprise), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.enterpriseStores(selectedEnterprise), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.concepts(selectedEnterprise), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(API_ENDPOINTS.franchisees(selectedEnterprise), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (menuResponse.ok) {
        const menuData = await menuResponse.json();
        // Handle both array response and wrapped response formats
        const menuList = Array.isArray(menuData) ? menuData : (menuData.data || menuData.menuItems || []);
        setMenuItems(menuList);
      }

      if (storesResponse.ok) {
        const storesData = await storesResponse.json();
        const storeList = Array.isArray(storesData) ? storesData : (storesData.data || storesData.stores || []);
        setStores(storeList);
      }

      if (conceptsResponse.ok) {
        const conceptsData = await conceptsResponse.json();
        const conceptList = Array.isArray(conceptsData) ? conceptsData : (conceptsData.data || conceptsData.concepts || []);
        setConcepts(conceptList);
      }

      if (franchiseesResponse.ok) {
        const franchiseesData = await franchiseesResponse.json();
        const franchiseeList = Array.isArray(franchiseesData) ? franchiseesData : (franchiseesData.data || franchiseesData.franchisees || []);
        setFranchisees(franchiseeList);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      setMessage('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const loadEffectivePrices = async () => {
    if (!selectedMenuItem || !selectedEnterprise) return;
    
    try {
      const prices: {[key: string]: number} = {};
      
      // Get effective price for each store
      const token = await getIdToken();
      const authHeader: HeadersInit = token ? { 'Authorization': `Bearer ${token}` } : {};
      for (const store of stores) {
        try {
          const response = await fetch(
            API_ENDPOINTS.pricingResolve(selectedMenuItem, store.storeId, selectedEnterprise),
            { headers: authHeader }
          );

          if (response.ok) {
            const data = await response.json();
            if (typeof data?.data?.effectivePrice === 'number') {
              prices[store.storeId] = data.data.effectivePrice;
            }
          }
        } catch (error) {
          console.warn(`Error getting price for store ${store.storeId}:`, error);
        }
      }
      
      setEffectivePrices(prices);
    } catch (error) {
      console.error('Error loading effective prices:', error);
    }
  };

  const handleSetPrice = async () => {
    if (!selectedMenuItem || !selectedLevelId || !newPrice) {
      setMessage('Please select menu item, level, and enter price');
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();

      const response = await fetch(API_ENDPOINTS.pricingSet(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          menuItemId: selectedMenuItem,
          levelType: selectedLevel,
          levelId: selectedLevelId,
          price: parseFloat(newPrice)
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage(`Price ${data.data.action} successfully!`);
        setNewPrice('');
        loadEffectivePrices(); // Refresh prices
      } else {
        const error = await response.json();
        setMessage(`Error: ${error.message}`);
      }

    } catch (error) {
      console.error('Error setting price:', error);
      setMessage('Error setting price');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveOverride = async (levelType: string, levelId: string) => {
    if (!selectedMenuItem) return;

    try {
      setLoading(true);
      const token = await getIdToken();

      const response = await fetch(API_ENDPOINTS.pricingRemove(), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          menuItemId: selectedMenuItem,
          levelType,
          levelId
        })
      });

      if (response.ok) {
        setMessage('Price override removed successfully!');
        loadEffectivePrices(); // Refresh prices
      } else {
        const error = await response.json();
        setMessage(`Error: ${error.message}`);
      }

    } catch (error) {
      console.error('Error removing override:', error);
      setMessage('Error removing price override');
    } finally {
      setLoading(false);
    }
  };

  const getLevelOptions = () => {
    switch (selectedLevel) {
      case 'concept':
        return concepts.map(c => ({ id: c.conceptId, name: c.name }));
      case 'franchisee':
        return franchisees.map(f => ({ id: f.franchiseeId, name: f.name }));
      case 'store':
        return stores.map(s => ({ id: s.storeId, name: `${s.name} (${s.code})` }));
      default:
        return [];
    }
  };

  const selectedItem = menuItems.find(item => item.menuItemId === selectedMenuItem);

  return (
    <div className="hierarchical-pricing-management">
      <div className="modern-card">
        <h2>Hierarchical Pricing Management</h2>
        <p>Set different prices for menu items at enterprise, concept, franchisee, or store levels.</p>

        {message && (
          <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
            {message}
          </div>
        )}

        <div className="pricing-controls">
          {/* Enterprise selection lives in the header strip — no in-page picker. */}

          {selectedEnterprise && (
            <div className="form-group">
              <label className="form-label">Menu Item</label>
              <select 
                value={selectedMenuItem} 
                onChange={(e) => setSelectedMenuItem(e.target.value)}
                className="form-control"
                disabled={!selectedEnterprise}
              >
                <option value="">Select menu item...</option>
                {menuItems.map(item => (
                  <option key={item.menuItemId} value={item.menuItemId}>
                    {item.name} - Base: ${item.basePrice || item.price}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Pricing Level</label>
            <select 
              value={selectedLevel} 
              onChange={(e) => {
                setSelectedLevel(e.target.value as any);
                setSelectedLevelId('');
              }}
              className="form-control"
              disabled={!selectedEnterprise}
            >
              <option value="concept">Concept Level</option>
              <option value="franchisee">Franchisee Level</option>
              <option value="store">Store Level</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">
              {selectedLevel === 'concept' ? 'Concept' : 
               selectedLevel === 'franchisee' ? 'Franchisee' : 'Store'}
            </label>
            <select 
              value={selectedLevelId} 
              onChange={(e) => setSelectedLevelId(e.target.value)}
              className="form-control"
              disabled={!selectedEnterprise}
            >
              <option value="">Select {selectedLevel}...</option>
              {getLevelOptions().map(option => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">New Price ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              className="form-control"
              placeholder="0.00"
              disabled={!selectedEnterprise || !selectedMenuItem}
            />
          </div>

          <div className="form-actions">
            <button
              onClick={handleSetPrice}
              disabled={loading || !selectedEnterprise || !selectedMenuItem || !selectedLevelId || !newPrice}
              className="btn btn-modern-primary"
            >
              {loading ? 'Setting Price...' : 'Set Price'}
            </button>
          </div>
        </div>

        {selectedItem && (
          <div className="pricing-preview">
            <h3>Current Pricing for "{selectedItem.name}"</h3>
            <div className="pricing-hierarchy">
              <div className="pricing-level">
                <strong>Enterprise Base Price:</strong> ${selectedItem.basePrice || selectedItem.price}
              </div>
              
              {stores.length > 0 && (
                <div className="store-prices">
                  <h4>Effective Prices by Store:</h4>
                  <div className="store-pricing-grid">
                    {stores.map(store => (
                      <div key={store.storeId} className="store-price-card">
                        <div className="store-name">{store.name} ({store.code})</div>
                        <div className="store-price">
                          ${effectivePrices[store.storeId]?.toFixed(2) || (selectedItem.basePrice || selectedItem.price).toFixed(2)}
                        </div>
                        <button
                          onClick={() => handleRemoveOverride('store', store.storeId)}
                          className="btn btn-outline btn-sm"
                          title="Remove store-specific pricing"
                        >
                          Reset to Default
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default HierarchicalPricingManagement;